/**
 * SampleSizeLabel — how much evidence backs a rendered rate, in plain words. Never renders or
 * implies "confidence" (PRD-04: the phrase "low confidence" tested as meaningless — "confidence in
 * what?"); a plain maturity label like "Early data · N scans" is the sanctioned shape.
 *
 * Extracted from the "Early data · N scans" pattern at
 * the retired legacy `/app/competitors` page's `sov-trend-hero-card.tsx` (T28 removed it; see git history), which was the
 * one place this exact copy is authored — a second call site imports this component rather than
 * re-typing the string.
 *
 * Three tiers, both thresholds taken from the source: below `EARLY_THRESHOLD` the source's own
 * branch point (`scanCount < 3`) — the sample is still forming and a click-triggered `InfoPopover`
 * explains why; from `EARLY_THRESHOLD` up to `SETTLED_THRESHOLD` the source's own copy ("They
 * settle into a reliable trend after about 5 scans") — the count is stated plainly with a light
 * maturity cue and no further caveat; at `SETTLED_THRESHOLD` and above — plain "Based on N …", no
 * caveat at all, matching the source's un-annotated branch exactly.
 *
 * DES-34: the source uses a hover `Tooltip` for this explanatory text, which is unreachable on
 * touch and unreadable to anyone who needs time — a documented DES-34 violation. This atom uses a
 * click-triggered `InfoPopover` instead and does not reproduce it.
 *
 * WHEN: beside any rendered rate/count backed by an `n` — `IntervalReadout` already delegates to
 *   this rather than formatting its own count.
 * NEVER: format a bare count a second time next to this label — `int(n)` lives here once (DES-04).
 */
import { InfoPopover } from '@/components/shared/info-popover'
import { countLabel, EARLY_THRESHOLD, SETTLED_THRESHOLD } from '@/lib/format'
import { cn } from '@/lib/utils'

// The thresholds and `countLabel` live in `src/lib/format.ts` (R6.3 B1), where the one interval
// form (`intervalSegments`) and the email/PDF can reach them; re-exported so existing imports hold.
export { countLabel, EARLY_THRESHOLD, SETTLED_THRESHOLD }

export interface SampleSizeLabelProps {
  /** Sample size backing the rendered value. */
  n: number
  /** Unit word, singular — defaults to "scan" (the source's unit). */
  unit?: string
  className?: string
}

export function SampleSizeLabel({
  n,
  unit = 'scan',
  className,
}: SampleSizeLabelProps): React.ReactElement {
  const label = countLabel(n, unit)

  if (n < EARLY_THRESHOLD) {
    return (
      <span
        className={cn('inline-flex items-center gap-1 text-xs text-muted-foreground', className)}
      >
        Early data · {label}
        <InfoPopover title="Early data" triggerLabel="Why this counts as early data">
          These numbers are based on only {label}, so they may shift. They settle into a reliable
          trend after about {SETTLED_THRESHOLD} {unit}s.
        </InfoPopover>
      </span>
    )
  }

  if (n < SETTLED_THRESHOLD) {
    // "Still settling", not "Building trend". DES-43's own `why:` names "Building" as a word that
    // tested as MEANINGLESS to first-time users — building toward what, and is it finished? It is
    // the rule's cited incident, reused verbatim as a status word. "Still settling" says the same
    // thing in language that explains itself: the number is real, it is still moving, wait.
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        Still settling · {label}
      </span>
    )
  }

  return <span className={cn('text-xs text-muted-foreground', className)}>Based on {label}</span>
}
